/**
 * Extracts longitude and latitude coordinates from a geometry string.
 *
 * Expected format:
 * - Any string containing coordinates wrapped in parentheses
 *   e.g. `"POINT(85.324 27.7172)"`
 *
 * @param {string} geometry - Geometry string containing coordinates.
 * @returns {[number, number] | undefined}
 * Returns a tuple `[longitude, latitude]` if coordinates are found,
 * otherwise `undefined`.
 * @note For now it only supports point geom, add support for any valid shape types
 */

import { Entity, EntityItem, MediaItemDetailed, MediaResponse } from "../types";
import { hasValidGeometry } from "./geoUtils";

//@TODO: Support any geom type, now it only supports point geom
export function getCoordinateFromGeom(geometry: string) {
  const matchBetweenParenthesisRegx = /\(([^)]+)\)/;

  const match = matchBetweenParenthesisRegx.exec(geometry)?.[1]; // get 1st index since it doesn't contains parenthesis
  if (!match) return;
  return match
    .toString()
    ?.split(" ")
    .map((value) => Number(value));
}

// export function getCentroidOfShape(shape: any) {
//   if (!shape) return;
// }

/**
 * Check whether object is empty or not
 * */
export function isEmptyObj(obj: Object | Array<unknown>) {
  if (!obj) return true;

  if (typeof obj !== "object") return true;

  if (Array.isArray(obj) && obj.length < 0) return true;

  if (Object.keys(obj || {})?.length <= 0) return true;

  return false;
}

/**
 * Validates a redirect path to prevent unsafe or external redirects.
 *
 * This ensures the application only redirects to internal routes by:
 * - Allowing only relative paths that start with "/"
 * - Skip protocol-relative URLs (e.g. "//dangerous-site.com")
 *
 * @param path - Redirect path extracted from the URL
 * @returns `true` if the path is safe
 */
export function isValidRedirect(path: string | null) {
  return path && path.startsWith("/") && !path.startsWith("//");
}

// Function to determine if a color is dark (for text contrast)
export function isColorDark(hexColor: string) {
  try {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Calculate brightness based on YIQ formula
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
  } catch (error) {
    // Default to assuming it's dark
    return true;
  }
}

/**
 * Checks whether a File matches a given MIME type or MIME category.
 *
 * Supports both:
 *  - Exact MIME types (e.g. "image/png", "application/pdf")
 *  - Grouped types using prefixes (e.g. "image/" to match all image formats)
 *
 * @param file - The File object to validate.
 * @param fileType - A full MIME type or prefix to match against.
 * @returns `true` if the file's MIME type matches or starts with the given type, otherwise `false`.
 *
 * @example
 * isAFile(file, "image/")        // true for image/png, image/jpeg, etc.
 * isAFile(file, "application/pdf") // true only for PDFs
 */
export function isAFile(file: File, fileType: string) {
  if (!file || !fileType) return false;

  const type = file?.type;
  return type === fileType || type.startsWith(fileType);
}

export function getEntityCategory(entity: Record<string, unknown>) {
  if (entity?.media_type) {
    return "media";
  }

  // for geo and non-geo entity
  const isGeo = hasValidGeometry(entity as unknown as Entity);
  return isGeo ? "geo" : "non-geo";
}

export function removeTrailingSlash(str: string) {
  return str?.replace(/\/$/, "");
}
